import { observable, action } from 'mobx';
import { FormikHelpers } from 'formik';

import {
  API_FUNCTION_TYPE,
  API_ACCOUNT_FEES_SINGLE,
  API_ACCOUNT_FEES_SINGLE_MODEL,
  API_ACCOUNT_DETAIL_FEES_SINGLE,
  API_ACCOUNT_DETAIL_FEES_SINGLE_MODEL,
  API_ACCOUNT_DETAIL_FEES_CREATE,
} from 'api/endpoints';
import { ACCOUNTS_EDIT_PLAN } from 'pages/Accounts/Edit/accountEditRoutes';
import { SingleValueResponse } from 'common/interfaces';

import history from 'helpers/routerHistory';
import { FormStore } from '../formStore';
import { RootStore } from 'stores/rootStore';
import { SingleFeesData } from 'pages/Accounts/Edit/Fees/Fees';
import { ViewMode } from 'pages/Accounts/Edit/components/viewModes';
import { DetailPage } from 'pages/Accounts/Detail/enums';

export class WorkflowSingleFeesStore extends FormStore<SingleFeesData> {
  @observable tab = 0;
  accountId = '';
  endpoint: API_FUNCTION_TYPE;
  endpointModel: API_FUNCTION_TYPE;
  @observable draft = false;

  constructor(rootStore: RootStore, accountId: string, viewMode?: ViewMode) {
    super(rootStore);
    this.accountId = accountId;
    this.endpoint = viewMode === ViewMode.DETAIL ? API_ACCOUNT_DETAIL_FEES_SINGLE : API_ACCOUNT_FEES_SINGLE;
    this.endpointModel =
      viewMode === ViewMode.DETAIL ? API_ACCOUNT_DETAIL_FEES_SINGLE_MODEL : API_ACCOUNT_FEES_SINGLE_MODEL;
  }

  @action
  setDraft = async () => {
    this.rootStore.stores.uiStore.setLoading(true);
    const response = await this.rootStore.stores.apiStore.patch<SingleValueResponse<boolean>>(
      API_ACCOUNT_DETAIL_FEES_CREATE(this.accountId),
    );
    if (response?.data?.value) {
      this.draft = true;
    }
    this.rootStore.stores.uiStore.setLoading(false);
  };

  @action
  prepareData = () => {
    if (this.data !== null) {
      const tempData: SingleFeesData = JSON.parse(JSON.stringify(this.data));

      if (!tempData.individualFee) {
        tempData.individualFee = {
          value: this.data.calculatedFee.value,
          currency: this.data.calculatedFee.currency,
        };
      }
      if (tempData.individualRate === null || tempData.individualRate === undefined) {
        tempData.individualRate = this.data.feeRate;
      }
      if (this.data.type === 'CONTINUOUS') {
        tempData.length = {
          years: this.data.options.years[this.data.options.years.length - 1],
          months: 0,
        };
      }
      tempData.individualRate = Math.round(tempData.individualRate * 10000) / 100;

      this.data = JSON.parse(JSON.stringify(tempData));
    }
  };

  @action
  fetchFees = async () => {
    this.rootStore.stores.uiStore.setLoading(true);
    await this.fetchData(`${this.endpoint(this.accountId)}`, { resetData: false });
    this.prepareData();
    this.draft = !!this.data?.draftExists;
    this.rootStore.stores.uiStore.setLoading(false);
  };

  handleSubmit = async (data: SingleFeesData, formikHelpers: FormikHelpers<SingleFeesData>) => {
    if (data.individualRate) {
      data.individualRate /= 100;
    }
    if (!data.type && this.data) {
      // in detail
      data.type = this.data.type;
    }
    if (data.draftExists) {
      delete data.draftExists;
    }
    return await this.submitData(`${this.endpoint(this.accountId)}`, data, {
      formikHelpers,
      singleValueResponse: true,
      validationPrefixDepth: 3,
    });
  };

  @action
  refresh = async (data: SingleFeesData, formikHelpers: FormikHelpers<SingleFeesData>) => {
    this.rootStore.stores.uiStore.setLoading(true);

    const tempData: SingleFeesData = JSON.parse(JSON.stringify(data));
    if (tempData.individualRate) {
      tempData.individualRate /= 100;
    }

    const ok = await this.submitData(`${this.endpointModel(this.accountId)}`, tempData, {
      formikHelpers,
      singleValueResponse: false,
      validationPrefixDepth: 3,
    });
    if (ok) {
      this.prepareData();
    } else {
      if (this.data !== null && data.individualRate) {
      }
    }
    this.rootStore.stores.uiStore.setLoading(false);
  };

  @action handleNext = async (
    data: SingleFeesData,
    formikHelpers: FormikHelpers<SingleFeesData>,
    tabChange?: Function,
    skip?: boolean,
    goFwd?: () => void
  ) => {
    const submitted = skip ? true : await this.handleSubmit(data, formikHelpers);
    if (submitted) {
      if (goFwd) goFwd();
      if (tabChange) tabChange(DetailPage.REGULAR_FEES);
    }
  };

  @action handleBack = (goBack?: () => void) => {
    goBack ? goBack() : history.push(ACCOUNTS_EDIT_PLAN(this.accountId));
  };
}
